import React, { useEffect, useMemo, useState } from "react";
import DataTable, { ColumnDef } from "@/components/utils/DataTable";
import { pageStyles } from "@/components/utils/PageLayout";
import { Button, loadingStats } from "@/components/ui";
import type { StatItem } from "@/components/ui";
import { sellValidationAPI } from "@/services/api";
import { formatDate, formatNumber, formatQuantity } from "@/utils/formatters";
import { sumBy } from "@/utils/aggregate";

type SellRow = {
    client_code: string;
    client_name: string;
    scheme: string;
    symbol: string;
    holding_quantity: number;
    sell_quantity: number;
    remaining_quantity: number;
    sell_amount: number;
};

type SellValidationResponse = {
    holding_date: string;
    count: number;
    data: SellRow[];
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const NUMBER_FIELDS: (keyof SellRow)[] = [
    "holding_quantity", "sell_quantity", "remaining_quantity", "sell_amount",
];

const SellValidation: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<SellValidationResponse | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [shortOnly, setShortOnly] = useState(false);
    // Ascending by default so the short (negative) positions surface first — they
    // are the reason this page exists.
    const [sortBy, setSortBy] = useState<keyof SellRow>("remaining_quantity");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await sellValidationAPI.getSellValidation();
            setData(res);
            setCurrentPage(1);
        } catch (e) {
            const apiErr = e as { response?: { data?: { error?: string } }; message?: string };
            setError(apiErr?.response?.data?.error || apiErr?.message || "Failed to fetch sell validation data.");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSort = (field: string) => {
        const f = field as keyof SellRow;
        if (sortBy === f) {
            setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(f);
            setSortDir(f === "remaining_quantity" ? "asc" : "desc");
        }
        setCurrentPage(1);
    };

    const filteredAndSorted = useMemo(() => {
        if (!data) return [] as SellRow[];
        const query = searchTerm.toLowerCase();

        const result = data.data.filter(r =>
            (!shortOnly || r.remaining_quantity < 0) && (
                (r.client_name || "").toLowerCase().includes(query) ||
                (r.client_code || "").toLowerCase().includes(query) ||
                (r.symbol || "").toLowerCase().includes(query) ||
                (r.scheme || "").toLowerCase().includes(query)
            )
        );

        return [...result].sort((a, b) => {
            let valA: any = a[sortBy];
            let valB: any = b[sortBy];

            if (NUMBER_FIELDS.includes(sortBy)) {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
            } else {
                valA = String(valA ?? "").toLowerCase();
                valB = String(valB ?? "").toLowerCase();
            }

            if (valA < valB) return sortDir === "asc" ? -1 : 1;
            if (valA > valB) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
    }, [data, searchTerm, shortOnly, sortBy, sortDir]);

    const totalCount = filteredAndSorted.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const paginatedData = useMemo(
        () => filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [filteredAndSorted, currentPage, pageSize],
    );

    const shortCount = useMemo(
        () => (data?.data ?? []).filter(r => r.remaining_quantity < 0).length,
        [data],
    );

    // Mirrors the on-screen column order, so the export cannot drift from the table.
    const csvColumns: { header: string; value: (r: SellRow) => string | number }[] = [
        { header: "Client Code", value: r => r.client_code || "" },
        { header: "Client Name", value: r => r.client_name || "" },
        { header: "Scheme", value: r => r.scheme || "" },
        { header: "Symbol", value: r => r.symbol || "" },
        { header: "Holding Quantity", value: r => r.holding_quantity ?? 0 },
        { header: "Sell Quantity", value: r => r.sell_quantity ?? 0 },
        { header: "Remaining Quantity", value: r => r.remaining_quantity ?? 0 },
        { header: "Sell Amount", value: r => r.sell_amount ?? 0 },
    ];

    const downloadCSV = () => {
        if (!filteredAndSorted.length) return;
        const esc = (v: string | number) =>
            typeof v === "number" ? String(v) : `"${v.replace(/"/g, '""')}"`;
        const csv = [
            csvColumns.map(c => esc(c.header)).join(","),
            ...filteredAndSorted.map(r => csvColumns.map(c => esc(c.value(r))).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `sell_validation_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    const columns: ColumnDef<SellRow>[] = [
        {
            key: "client_code",
            header: "Client Code",
            sortable: true,
            render: row => <span className={pageStyles['cell-strong']}>{row.client_code}</span>,
            renderFooter: () => "Totals",
        },
        {
            key: "client_name",
            header: "Client Name",
            sortable: true,
            render: row => row.client_name,
            renderFooter: rows => (
                <span className={pageStyles['cell-sub']}>
                    {rows.length} sell order{rows.length === 1 ? "" : "s"} (all pages)
                </span>
            ),
        },
        { key: "scheme", header: "Scheme", sortable: true, render: row => row.scheme },
        { key: "symbol", header: "Symbol", sortable: true, render: row => row.symbol },
        { key: "holding_quantity", header: "Holding Qty", sortable: true, align: "right", render: row => formatQuantity(row.holding_quantity) },
        {
            key: "sell_quantity",
            header: "Sell Qty",
            sortable: true,
            align: "right",
            render: row => formatQuantity(row.sell_quantity),
            renderFooter: rows => formatQuantity(sumBy(rows, "sell_quantity")),
        },
        {
            key: "remaining_quantity",
            header: "Remaining Qty",
            sortable: true,
            align: "right",
            // A negative remainder means the order sells more than the client holds.
            render: row => (
                <span
                    style={{
                        color: row.remaining_quantity < 0 ? "var(--neg)" : undefined,
                        fontWeight: row.remaining_quantity < 0 ? 600 : undefined,
                    }}
                >
                    {formatQuantity(row.remaining_quantity)}
                </span>
            ),
        },
        {
            key: "sell_amount",
            header: "Sell Amount",
            sortable: true,
            align: "right",
            render: row => formatNumber(row.sell_amount || 0, 2),
            renderFooter: rows => formatNumber(sumBy(rows, "sell_amount"), 2),
        },
    ];

    const stats: StatItem[] | undefined = loading
        ? loadingStats(["Holding date", "Orders validated", "Short positions"])
        : data ? [
        { label: "Holding date", value: data.holding_date ? formatDate(data.holding_date) : "—" },
        { label: "Orders validated", value: formatNumber(data.count, 0) },
        {
            label: "Short positions",
            value: formatNumber(shortCount, 0),
            hint: shortCount > 0 ? "Selling more than the client holds" : "Every order is covered",
        },
    ] : undefined;

    const toolbarSlot = shortCount > 0 ? (
        <div className={pageStyles['page-toolbar']}>
            <Button
                variant={shortOnly ? "warning" : "secondary"}
                size="sm"
                onClick={() => { setShortOnly(prev => !prev); setCurrentPage(1); }}
            >
                {shortCount} short position{shortCount === 1 ? "" : "s"}
                {shortOnly ? " — showing only" : ""}
            </Button>
        </div>
    ) : undefined;

    return (
        <DataTable
            title="Sell Validation"
            subtitle="Pending sell orders checked against the holdings on the last processed date"
            stats={stats}
            data={paginatedData}
            footerRows={filteredAndSorted}
            columns={columns}
            rowKey={(row, index) => `${row.client_code}-${row.symbol}-${index}`}
            totalCount={totalCount}
            loading={loading}
            loadingMessage="Loading sell validation…"
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
            sortField={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            searchQuery={searchTerm}
            onSearchChange={value => { setSearchTerm(value); setCurrentPage(1); }}
            searchPlaceholder="Search by client, code, symbol or scheme…"
            toolbarSlot={toolbarSlot}
            onDownloadCSV={downloadCSV}
            headerActions={
                <Button onClick={fetchData} disabled={loading}>
                    {loading ? "Refreshing…" : "Refresh"}
                </Button>
            }
            emptyMessage={shortOnly ? "No short positions found." : "No sell orders found."}
            fitContent
        />
    );
};

export default SellValidation;
